import { apiConfig } from '@/configs/apiConfig';
import axiosClient from '@/libs/axios/instance.client';
import type {
    TranslationPayload,
    TranslationsResponse,
} from '@/types/apps/translation';
import type {
    ApiResponse,
    PaginatedResult,
    QueryParams,
} from '@/types/common/api.type';
import type { TranslationEnum } from '@/types/enums/translation.enum';
import type { BaseApiClientInterface } from '@/types/service/base-api-client.interface';
import type { Indexable } from '@/types/service/indexable.interface';

type TranslationListResponse = ApiResponse<
    PaginatedResult<TranslationsResponse>
>;

export class TranslationService implements Indexable {
    public constructor(private apiClient: BaseApiClientInterface) {}

    public endpoints = {
        base: () => '/api/translations',
        detail: (id: string) => `/api/translations/${id}`,
        import: () => '/api/translations/import/web',
        export: () => '/api/translations/export',
        keyValue: () => '/api/translations/key-value',
    };

    public index(params?: QueryParams): Promise<TranslationListResponse> {
        return this.apiClient.fetchApi(this.endpoints.base(), params);
    }

    public update(
        id: string,
        payload: TranslationPayload
    ): Promise<ApiResponse<TranslationsResponse>> {
        return this.apiClient.putApi(this.endpoints.detail(id), payload);
    }

    public async importFile(file: File): Promise<any> {
        const form = this.apiClient.buildFormData({ file });

        return this.apiClient.postApi<any>(this.endpoints.import(), form);
    }

    public async exportFile({
        langConfig,
    }: {
        langConfig: TranslationEnum;
    }): Promise<Blob> {
        const response = await axiosClient.get(this.endpoints.export(), {
            responseType: 'blob',
            headers: {
                'X-Override-Lang': 'true',
                'Accept-Language': langConfig,
            },
        });

        return response.data; // Blob
    }

    public async keyValue(params: any): Promise<any> {
        const response = await fetch(
            `${apiConfig.baseUrl}${this.endpoints.keyValue()}?clientContext=web`,
            {
                headers: {
                    'Accept-Language': params.languageCode,
                },
                method: 'GET',
            }
        );

        return response;
    }
}
